import { ArrowBack, Edit, Email, Print } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AppDispatch, RootState } from 'app/store';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import {
  fetchClientAndOrgInfo,
  fetchSingleInvoice,
  sendInvoice
} from 'slices/NdisSlices/finance/InvoiceSlice';
import { Invoice } from 'types/ndisSection/invoiceTypes';
import { mapToUIMappedInvoice } from './FinanceTab';
import PrintComponent from './PrintComponent';

interface InvoiceViewProps {
  invoice: Invoice;
  onEdit: (invoice: Invoice) => void;
  onBack: () => void;
  isSmallScreen: boolean;
  fortnightDate: string;
}

const InvoiceView: React.FC<InvoiceViewProps> = ({
  invoice,
  onEdit,
  onBack,
  isSmallScreen,
  fortnightDate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
  const { current_invoice, loading, error, client, sending, sendError } = useTypedSelector(
    (state) =>
      state.invoice || {
        current_invoice: null,
        loading: false,
        error: null,
        client: null,
        sending: false,
        sendError: null
      }
  );
  const selectedProjectId = useTypedSelector(
    (state) => (state as any).homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useTypedSelector(
    (state) => state.serviceSelected.selectedServiceTitle || ''
  );
  const componentRef = useRef<HTMLDivElement>(null);

  const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');

  useEffect(() => {
    if (!invoice.invoice_number) {
      console.error('Invoice number is missing');
      return;
    }

    // Fetch invoice
    const filterDate = fortnightDate
      ? new Date(fortnightDate.split(' - ')[0].split(': ')[1].split('/').reverse().join('-'))
          .toISOString()
          .split('T')[0]
      : new Date().toISOString().split('T')[0];
    const invoicePayload = {
      user_whoRequested_id: parsedUserDetails.name || '',
      group_id: selectedProjectId || '',
      service_name_id: (selectedServiceTitle || '').replace(' ', '_'),
      nested_component_id: 'Invoice',
      invoice_id: invoice.invoice_number,
      client_name: invoice.customer_name || '',
      filter_date: filterDate,
      client_key: invoice?.client_key || ''
    };
    dispatch(fetchSingleInvoice(invoicePayload));

    // Fetch client and organization info
    const clientOrgPayload = {
      group_id: selectedProjectId || '',
      service_name_id: selectedServiceTitle || '',
      client_name: invoice?.client_key || invoice.customer_name || ''
    };
    dispatch(fetchClientAndOrgInfo(clientOrgPayload));
  }, [
    dispatch,
    invoice.invoice_number,
    invoice.customer_name,
    parsedUserDetails.name,
    selectedProjectId,
    selectedServiceTitle,
    fortnightDate
  ]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          margin: 0;
          padding: 0;
          background: white;
        }
        .no-print { display: none !important; }
        html, body {
          width: 210mm;
          height: 297mm;
        }
      }
    `
  });

  const { client: invoiceClient } = useTypedSelector(
    (state) => state.invoice || { organization: null, client: null }
  );

  // console.log('client', invoiceClient?.manager_email);

  const handleSendInvoice = async () => {
    if (!componentRef.current || !displayInvoice) {
      console.error('No invoice content available to send');
      return;
    }

    if (!client?.email) {
      console.error('Client email is missing');
      return;
    }

    try {
      // Use html2canvas to capture the PrintComponent as an image
      const canvas = await html2canvas(componentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123
      });

      // Create a new jsPDF instance
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add the first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content exceeds one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Convert PDF to Base64
      const pdfBase64 = pdf.output('datauristring').split(',')[1];

      // Validate Base64 string
      const isValidBase64 = pdfBase64.startsWith('JVBERi0xLjQK');
      console.log(
        'Base64 Validation:',
        isValidBase64 ? 'Valid PDF (starts with %PDF-1.4)' : 'Invalid PDF header'
      );
      console.log('PDF Base64:', pdfBase64);

      // Dispatch sendInvoice action
      await dispatch(
        sendInvoice({
          email: invoiceClient?.manager_email || '',
          client_name: invoiceClient?.client_name || '',
          pdf_base64: pdfBase64,
          filename: `invoice-${displayInvoice.invoiceNumber}_${invoiceClient?.client_name}.pdf`,
          invoice_number: displayInvoice.invoiceNumber,
          plan_manager_name: '',
          group_id: selectedProjectId,
          service_id: selectedServiceTitle
        })
      );

      // For debugging, download the PDF to verify content
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${displayInvoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('PDF downloaded as invoice-', displayInvoice.invoiceNumber, '.pdf');
    } catch (error: any) {
      console.error('Error generating PDF:', error.message);
    }
  };

  const selectedInvoice = Array.isArray(current_invoice)
    ? current_invoice.find((inv) => inv.invoice_number === invoice.invoice_number) || null
    : current_invoice;

  const displayInvoice = selectedInvoice ? mapToUIMappedInvoice(selectedInvoice) : null;
  console.log('selectedInvoice', selectedInvoice);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[100],
        overflow: 'hidden',
        p: 2
      }}
    >
      {/* Header with actions */}
      <Box
        className="no-print"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={onBack} size={isMobile ? 'small' : 'medium'}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" noWrap>
            Invoice #{displayInvoice?.invoiceNumber || invoice.invoice_number || 'Loading...'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => onEdit(invoice)}
              startIcon={<Edit />}
              size={isMobile ? 'small' : 'medium'}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrint}
              startIcon={<Print />}
              size={isMobile ? 'small' : 'medium'}
              disabled={loading || !displayInvoice}
            >
              Print
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSendInvoice}
              startIcon={<Email />}
              size={isMobile ? 'small' : 'medium'}
              disabled={loading || sending || !displayInvoice || !client?.email}
            >
              Send Invoice
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {sendError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sendError}
          </Alert>
        )}
        {loading || sending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress />
          </Box>
        ) : displayInvoice ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box
              ref={componentRef}
              sx={{
                width: '100%',
                maxWidth: '210mm',
                minHeight: '297mm',
                p: { xs: 2, sm: 2 }
              }}
            >
              <PrintComponent invoiceData={displayInvoice} />
            </Box>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Invoice not found
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default InvoiceView;
